import React from 'react';
import {
  Action, ConfirmModal, Portal, useModal, useNotifications,
} from '@doveit/bricks';
import { Offer } from '../../../providers/api/dtos';
import { cancelOfferAcceptanceSignatureRequest } from '../../../providers/api/offer/offerProvider';
import { DigitalOfferStatus } from '../../../domain/types';
import { useComputedOfferStatus } from '../../hooks/use-computed-offer-status/useComputedOfferStatus';

export const CANCEL_OFFER_ACCEPTANCE_SIGNATURE_REQUEST_SUCCESS_MESSAGE = 'La richiesta di accettazione della proposta e presa visione è stata correttamente annullata.';
export const CANCEL_OFFER_ACCEPTANCE_SIGNATURE_REQUEST_ERROR_MESSAGE = 'Non è stato possibile annullare la richesta di accettazione e presa visione della proposta.';
export const CANCEL_COUNTER_OFFER_ACCEPTANCE_SIGNATURE_REQUEST_SUCCESS_MESSAGE = 'La richiesta di controproposta è stata correttamente annullata.';
export const CANCEL_COUNTER_OFFER_ACCEPTANCE_SIGNATURE_REQUEST_ERROR_MESSAGE = 'Non è stato possibile annullare la richesta di controproposta.';

interface ChildFnProps {
  cancelSignatureRequest: VoidFunction,
  isLoading: boolean,
}

export interface CancelOfferAcceptanceSignatureRequestActionProps {
  offer: Offer,
  onSuccess?: VoidFunction,
  children?: (childFnProps: ChildFnProps) => React.ReactNode,
}

const CancelOfferAcceptanceSignatureRequestAction: React.FC<CancelOfferAcceptanceSignatureRequestActionProps> = ({
  offer,
  onSuccess,
  children,
}) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const { addSuccess, addError } = useNotifications();
  const cancelSignatureRequestModal = useModal();
  const { data: computedStatusData } = useComputedOfferStatus(offer?.intentId, offer);

  const openConfirmModal = React.useCallback(() => {
    cancelSignatureRequestModal.open();
  }, [cancelSignatureRequestModal]);

  const isWaitingForAcceptance = React.useMemo(
    () => computedStatusData?.status === DigitalOfferStatus.WAITING_FOR_ACCEPTANCE,
    [computedStatusData?.status],
  );

  const acceptanceSignatureRequest = React.useCallback(async () => {
    try {
      setIsLoading(true);

      await cancelOfferAcceptanceSignatureRequest(offer.id!);

      setIsLoading(false);
      cancelSignatureRequestModal.close();

      addSuccess(isWaitingForAcceptance
        ? CANCEL_OFFER_ACCEPTANCE_SIGNATURE_REQUEST_SUCCESS_MESSAGE
        : CANCEL_COUNTER_OFFER_ACCEPTANCE_SIGNATURE_REQUEST_SUCCESS_MESSAGE);
      onSuccess?.();
    } catch (_) {
      setIsLoading(false);
      addError(isWaitingForAcceptance
        ? CANCEL_OFFER_ACCEPTANCE_SIGNATURE_REQUEST_ERROR_MESSAGE
        : CANCEL_COUNTER_OFFER_ACCEPTANCE_SIGNATURE_REQUEST_ERROR_MESSAGE);
    }
  }, [offer.id, cancelSignatureRequestModal, addSuccess, isWaitingForAcceptance, onSuccess, addError]);

  if (computedStatusData?.status !== DigitalOfferStatus.WAITING_FOR_ACCEPTANCE && computedStatusData?.status !== DigitalOfferStatus.WAITING_FOR_COUNTER_OFFER_ACCEPTANCE) {
    return null;
  }

  return (
    <>
      {children
        ? children({ cancelSignatureRequest: openConfirmModal, isLoading })
        : (
          <Action
            aria-label={isWaitingForAcceptance
              ? 'Annulla la richiesta di accettazione e presa visione della proposta'
              : 'Annulla la richiesta di controproposta'}
            label={isWaitingForAcceptance
              ? 'Annulla accettazione'
              : 'Annulla controproposta'}
            onClick={openConfirmModal}
            size="S"
            loading={isLoading}
          />
        )}
      <Portal>
        <ConfirmModal
          {...cancelSignatureRequestModal}
          title={isWaitingForAcceptance
            ? 'Annulla accettazione e presa visione della proposta'
            : 'Annulla controproposta'}
          aria-label={isWaitingForAcceptance
            ? "Modale per annullare l'accettazione e presa visione della proposta"
            : 'Modale per annullare la richiesta di controproposta'}
          onConfirm={acceptanceSignatureRequest}
          onAbort={cancelSignatureRequestModal.close}
          saving={isLoading}
          variant="critical"
        >
          {isWaitingForAcceptance
            ? "Confermando l'operazione, la procedura digitale di accettazione e presa visione della proposta sarà annullata."
            : "Confermando l'operazione, la procedura digitale di controproposta sarà annullata."}
        </ConfirmModal>
      </Portal>
    </>
  );
};

export default CancelOfferAcceptanceSignatureRequestAction;
